import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  createAvalApi,
  deleteAllAvalsOfLineApi,
  deleteAvalApi,
  fetchAvalsByLine,
  importAvalsXlsx,
  patchAval,
  patchAvalLine,
  previewAvalsXlsx,
  renameAvalApi,
  type Aval,
  type AvalImportPreview,
  type AvalImportResult,
  type AvalLine,
  type AvalPeriodicity,
  type AvalStatus,
} from "../api";
import { formatIban } from "../lib/grouping";

/**
 * Detalle de una LÍNEA DE AVALES: muestra los avales que cuelgan de ella,
 * permite dar de alta uno nuevo y editar los existentes en línea (celda a
 * celda). Los campos nuevos que aporta ConciliaEcilimp por encima de lo que
 * envía el banco son: CECO, proyecto, estado, fecha posible vencimiento,
 * % comisión, periodicidad — y desde el cuadro se generan las cuotas
 * futuras hasta possibleMaturityDate o 31/12/2028.
 */

const PERIODICITY_LABELS: Record<AvalPeriodicity, string> = {
  monthly: "Mensual",
  quarterly: "Trimestral",
  semiannual: "Semestral",
  annual: "Anual",
};

const STATUSES: AvalStatus[] = ["Vigente", "Cancelado", "Ejecutado", "Vencido"];

function fmtMoney(n: number, currency: string): string {
  return n.toLocaleString("es-ES", { style: "currency", currency, minimumFractionDigits: 2 });
}

export function AvalLineDetail() {
  const params = useParams<{ bankCode: string; lineNumber: string }>();
  const bankCode = params.bankCode ?? "";
  const lineNumber = params.lineNumber ?? "";

  const [line, setLine] = useState<AvalLine | null>(null);
  const [avals, setAvals] = useState<Aval[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<Record<string, Partial<Aval>>>({});
  const [importPreview, setImportPreview] = useState<AvalImportPreview | null>(null);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importResult, setImportResult] = useState<AvalImportResult | null>(null);
  const [importBusy, setImportBusy] = useState(false);
  const [importOverwrite, setImportOverwrite] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const emptyDraft = (l: AvalLine | null) => ({
    avalNumber: "",
    amount: "",
    constitutionDate: "",
    possibleMaturityDate: "",
    reason: "",
    recipient: "",
    recipientCif: "",
    costingCode: "",
    projectCode: "",
    status: "Vigente" as AvalStatus,
    openingRatePercent: l?.defaultOpeningRatePercent != null ? String(l.defaultOpeningRatePercent) : "",
    commissionRateAnnual: l?.defaultCommissionRateAnnual != null ? String(l.defaultCommissionRateAnnual) : "",
    periodicity: (l?.defaultPeriodicity ?? "quarterly") as AvalPeriodicity,
    commissionMinimum: l?.defaultCommissionMinimum != null ? String(l.defaultCommissionMinimum) : "",
    notes: "",
  });
  const [draft, setDraft] = useState(emptyDraft(null));

  const load = async () => {
    setError(null);
    try {
      const r = await fetchAvalsByLine(bankCode, lineNumber);
      setLine(r.line);
      setAvals(r.avals);
      setDraft((d) => (d.avalNumber === "" ? emptyDraft(r.line) : d));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bankCode, lineNumber]);

  const totalVigente = useMemo(() => {
    if (!avals) return 0;
    return avals.filter((a) => a.status === "Vigente").reduce((s, a) => s + a.amount, 0);
  }, [avals]);

  const parseNumOrNull = (s: string): number | null => {
    const t = s.trim().replace(",", ".");
    if (!t) return null;
    const n = Number(t);
    return Number.isFinite(n) ? n : null;
  };

  // Input controlado para valores numéricos: mantiene la cadena bruta
  // mientras el usuario escribe (para permitir "0.", "0.1", coma decimal,
  // etc.) y solo confirma el número parseado en blur. Antes se hacía todo
  // controlado con el número, y React sobrescribía el "." en cada tecla,
  // impidiendo introducir decimales.
  const NumCell = ({
    value,
    onCommit,
    allowNull = true,
    className = "num",
    placeholder,
    title,
  }: {
    value: number | null;
    onCommit: (v: number | null) => void;
    allowNull?: boolean;
    className?: string;
    placeholder?: string;
    title?: string;
  }) => {
    const [draft, setDraft] = useState<string | null>(null);
    const shown = draft !== null ? draft : value !== null && value !== undefined ? String(value) : "";
    return (
      <input
        className={className}
        value={shown}
        placeholder={placeholder}
        title={title}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={(e) => {
          setDraft(null);
          const parsed = parseNumOrNull(e.target.value);
          onCommit(allowNull ? parsed : parsed ?? 0);
        }}
      />
    );
  };

  const parseDateOrNull = (s: string): string | null => {
    const t = s.trim();
    return /^\d{4}-\d{2}-\d{2}$/.test(t) ? t : null;
  };

  const save = async () => {
    if (!line) return;
    if (!draft.avalNumber.trim()) return alert("Indica el nº de aval.");
    const amount = parseNumOrNull(draft.amount);
    if (amount === null || amount <= 0) return alert("Importe > 0 obligatorio.");
    setBusy(true);
    try {
      await createAvalApi({
        bankCode,
        lineNumber,
        avalNumber: draft.avalNumber.trim(),
        amount,
        currency: line.currency,
        constitutionDate: parseDateOrNull(draft.constitutionDate),
        possibleMaturityDate: parseDateOrNull(draft.possibleMaturityDate),
        reason: draft.reason.trim() || undefined,
        recipient: draft.recipient.trim() || undefined,
        recipientCif: draft.recipientCif.trim() || undefined,
        costingCode: draft.costingCode.trim() || undefined,
        projectCode: draft.projectCode.trim() || undefined,
        status: draft.status,
        openingRatePercent: parseNumOrNull(draft.openingRatePercent),
        commissionRateAnnual: parseNumOrNull(draft.commissionRateAnnual),
        periodicity: draft.periodicity,
        commissionMinimum: parseNumOrNull(draft.commissionMinimum),
        notes: draft.notes.trim() || undefined,
      });
      setDraft(emptyDraft(line));
      setCreating(false);
      load();
    } catch (e: unknown) {
      alert(`Error al crear: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  };

  const commitField = async (avalNumber: string, patch: Partial<Aval>) => {
    try {
      await patchAval(bankCode, lineNumber, avalNumber, patch);
      setEditing((prev) => {
        const copy = { ...prev };
        delete copy[avalNumber];
        return copy;
      });
      load();
    } catch (e: unknown) {
      alert(`Error al guardar: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  const onDelete = async (a: Aval) => {
    if (!confirm(`¿Borrar aval ${a.avalNumber}?`)) return;
    try {
      await deleteAvalApi(bankCode, lineNumber, a.avalNumber);
      load();
    } catch (e: unknown) {
      alert(`No se ha podido borrar: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  const onRename = async (a: Aval) => {
    const newNum = prompt(`Renombrar aval\n\nNº actual: ${a.avalNumber}\n\nEscribe el nuevo nº:`, a.avalNumber);
    if (newNum === null) return;
    const trimmed = newNum.trim();
    if (!trimmed || trimmed === a.avalNumber) return;
    try {
      await renameAvalApi(bankCode, lineNumber, a.avalNumber, trimmed);
      // Limpia el estado de edición del avalNumber viejo (si tenía cambios
      // pendientes en memoria) — ya no aplica al nuevo id.
      setEditing((prev) => {
        if (!(a.avalNumber in prev)) return prev;
        const copy = { ...prev };
        delete copy[a.avalNumber];
        return copy;
      });
      load();
    } catch (e: unknown) {
      alert(`No se ha podido renombrar: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  const onDeleteAll = async () => {
    if (!avals || avals.length === 0) return;
    const first = confirm(
      `¿Borrar TODOS los ${avals.length} avales de esta línea?\n\n` +
      `La línea (defaults, IBAN, alias) se conserva. Los cuadros de comisiones ` +
      `también se borran. Útil para reimportar limpio tras un import fallido.`,
    );
    if (!first) return;
    const second = confirm(
      `Última confirmación: se van a borrar ${avals.length} avales.\n\n` +
      `Esto NO se puede deshacer. ¿Continuar?`,
    );
    if (!second) return;
    try {
      const r = await deleteAllAvalsOfLineApi(bankCode, lineNumber);
      alert(`${r.deleted} avales borrados. Ya puedes reimportar.`);
      load();
    } catch (e: unknown) {
      alert(`No se ha podido borrar: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  const onPickFile = async (file: File) => {
    setImportFile(file);
    setImportResult(null);
    setImportPreview(null);
    setImportBusy(true);
    try {
      const preview = await previewAvalsXlsx(bankCode, lineNumber, file);
      setImportPreview(preview);
    } catch (e: unknown) {
      alert(`Error al leer el Excel: ${e instanceof Error ? e.message : String(e)}`);
      setImportFile(null);
    } finally {
      setImportBusy(false);
    }
  };

  const doImport = async () => {
    if (!importFile) return;
    setImportBusy(true);
    try {
      const result = await importAvalsXlsx(bankCode, lineNumber, importFile, importOverwrite);
      setImportResult(result);
      setImportPreview(null);
      setImportFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      load();
    } catch (e: unknown) {
      alert(`Error al importar: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setImportBusy(false);
    }
  };

  const cancelImport = () => {
    setImportFile(null);
    setImportPreview(null);
    setImportResult(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const updateLineDefault = async (patch: Partial<AvalLine>) => {
    try {
      const updated = await patchAvalLine(bankCode, lineNumber, patch);
      setLine(updated);
    } catch (e: unknown) {
      alert(`Error: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  if (error) {
    return (
      <section className="card">
        <p className="error">Error: {error}</p>
        <p><Link to="/avales">← Volver a Líneas de Avales</Link></p>
      </section>
    );
  }
  if (!line || !avals) {
    return <section className="card"><p>Cargando…</p></section>;
  }

  return (
    <section className="card" style={{ maxWidth: "none", width: "100%" }}>
      <div className="card-head">
        <div>
          <h2>
            <Link to="/avales" style={{ fontWeight: 400, opacity: 0.7 }}>Avales</Link>
            {" · "}
            {line.alias || `${line.bankName || line.bankCode} · Línea ${line.lineNumber}`}
          </h2>
          <p className="meta">
            {line.bankName || line.bankCode} · Nº línea {line.lineNumber} · IBAN {line.iban ? formatIban(line.iban) : "—"} · {avals.length} aval(es) · Total vigente {fmtMoney(totalVigente, line.currency)}
          </p>
        </div>
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <button type="button" onClick={() => setCreating((x) => !x)}>
            {creating ? "✕ Cancelar" : "+ Nuevo aval"}
          </button>
          {avals.length > 0 && (
            <button
              type="button"
              onClick={onDeleteAll}
              title="Borra todos los avales de la línea (útil para reimportar limpio). La línea se conserva."
              style={{ color: "var(--warning, #b30)" }}
            >
              🗑 Borrar todos ({avals.length})
            </button>
          )}
          <label style={{ display: "inline-block" }}>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xlsm,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              style={{ display: "none" }}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void onPickFile(f);
              }}
            />
            <button type="button" onClick={() => fileInputRef.current?.click()} disabled={importBusy}>
              📥 Importar Excel
            </button>
          </label>
          <button type="button" onClick={load}>Recargar</button>
        </div>
      </div>

      {(importPreview || importResult || importBusy) && (
        <div style={{ padding: "0.75rem", background: "rgba(120,150,200,0.08)", borderRadius: 6, margin: "0.5rem 0" }}>
          {importBusy && !importPreview && <p>⏳ Leyendo Excel…</p>}
          {importPreview && (
            <>
              <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap", alignItems: "center" }}>
                <b>Preview del Excel</b>
                <span className="meta">{importPreview.rowsCount} fila(s) detectadas</span>
                <span className="meta">Cabecera en fila {importPreview.headerRowIndex}</span>
                {importPreview.errors.length > 0 && (
                  <span className="warning">{importPreview.errors.length} error(es)</span>
                )}
              </div>
              <div className="meta" style={{ marginTop: "0.25rem" }}>
                <b>Reconocidas:</b> {importPreview.headersDetected.join(" · ") || "(ninguna)"}
              </div>
              {importPreview.headersRaw.length > importPreview.headersDetected.length && (
                <div className="meta" style={{ marginTop: "0.25rem", color: "var(--warning, #b30)" }}>
                  <b>Ignoradas:</b>{" "}
                  {importPreview.headersRaw
                    .filter((h) => !importPreview.headersDetected.includes(h))
                    .join(" · ")}
                </div>
              )}
              {importPreview.errors.length > 0 && (
                <ul style={{ marginTop: "0.5rem", color: "var(--warning, #b30)" }}>
                  {importPreview.errors.slice(0, 10).map((e, i) => (
                    <li key={i}>Fila {e.row}: {e.message}</li>
                  ))}
                </ul>
              )}
              {importPreview.rowsCount > 0 && (
                <div style={{ maxHeight: "18rem", overflow: "auto", marginTop: "0.5rem", border: "1px solid var(--border, #ccc)", borderRadius: 4 }}>
                  <table className="table" style={{ fontSize: "0.8em" }}>
                    <thead>
                      <tr>
                        <th>Nº</th>
                        <th>Fecha</th>
                        <th>Importe</th>
                        <th>Receptor</th>
                        <th>CIF</th>
                        <th>CECO</th>
                        <th>Proy.</th>
                        <th>Motivo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importPreview.rows.slice(0, 100).map((r) => (
                        <tr key={r.sourceRow}>
                          <td>{r.avalNumber}</td>
                          <td>{r.constitutionDate ?? "—"}</td>
                          <td style={{ textAlign: "right" }}>{r.amount.toLocaleString("es-ES", { minimumFractionDigits: 2 })}</td>
                          <td>{r.recipient ?? ""}</td>
                          <td>{r.recipientCif ?? ""}</td>
                          <td>{r.costingCode ?? ""}</td>
                          <td>{r.projectCode ?? ""}</td>
                          <td style={{ maxWidth: "20rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.reason ?? ""}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div style={{ marginTop: "0.75rem", display: "flex", gap: "0.5rem", alignItems: "center" }}>
                <label style={{ fontSize: "0.85em" }}>
                  <input type="checkbox" checked={importOverwrite} onChange={(e) => setImportOverwrite(e.target.checked)} />
                  {" Sobrescribir avales existentes (si no, los duplicados se saltan)"}
                </label>
                <button type="button" onClick={doImport} disabled={importBusy || importPreview.rowsCount === 0}>
                  {importBusy ? "Importando…" : `Importar ${importPreview.rowsCount} aval(es)`}
                </button>
                <button type="button" onClick={cancelImport}>Cancelar</button>
              </div>
            </>
          )}
          {importResult && (
            <>
              <b>Import completado</b>
              <ul style={{ marginTop: "0.5rem" }}>
                <li>Creados: {importResult.created}</li>
                <li>Actualizados: {importResult.updated}</li>
                <li>Saltados (ya existían): {importResult.skipped}</li>
                {importResult.errors.length > 0 && <li className="warning">Errores: {importResult.errors.length}</li>}
              </ul>
              {importResult.errors.length > 0 && (
                <ul style={{ color: "var(--warning, #b30)" }}>
                  {importResult.errors.slice(0, 20).map((e, i) => (
                    <li key={i}>Fila {e.row}: {e.message}</li>
                  ))}
                </ul>
              )}
              <button type="button" onClick={() => setImportResult(null)} style={{ marginTop: "0.5rem" }}>Cerrar</button>
            </>
          )}
        </div>
      )}

      <details style={{ margin: "0.5rem 0 1rem" }}>
        <summary style={{ cursor: "pointer" }}>Condiciones por defecto de la línea (heredadas al alta de nuevos avales)</summary>
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "flex-end", paddingTop: "0.5rem" }}>
          <label style={{ fontSize: "0.85em" }}>
            % apertura<br />
            <input
              defaultValue={line.defaultOpeningRatePercent ?? ""}
              onBlur={(e) => updateLineDefault({ defaultOpeningRatePercent: parseNumOrNull(e.target.value) })}
              style={{ width: "5rem" }}
            />
          </label>
          <label style={{ fontSize: "0.85em" }}>
            % anual<br />
            <input
              defaultValue={line.defaultCommissionRateAnnual ?? ""}
              onBlur={(e) => updateLineDefault({ defaultCommissionRateAnnual: parseNumOrNull(e.target.value) })}
              style={{ width: "5rem" }}
            />
          </label>
          <label style={{ fontSize: "0.85em" }}>
            Periodicidad<br />
            <select
              defaultValue={line.defaultPeriodicity}
              onChange={(e) => updateLineDefault({ defaultPeriodicity: e.target.value as AvalPeriodicity })}
            >
              {(Object.keys(PERIODICITY_LABELS) as AvalPeriodicity[]).map((k) => (
                <option key={k} value={k}>{PERIODICITY_LABELS[k]}</option>
              ))}
            </select>
          </label>
          <label style={{ fontSize: "0.85em" }}>
            Mínimo comisión<br />
            <input
              defaultValue={line.defaultCommissionMinimum ?? ""}
              onBlur={(e) => updateLineDefault({ defaultCommissionMinimum: parseNumOrNull(e.target.value) })}
              style={{ width: "7rem" }}
            />
          </label>
        </div>
      </details>

      {creating && (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", padding: "0.75rem 0", borderTop: "1px solid var(--border)", borderBottom: "1px solid var(--border)" }}>
          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "flex-end" }}>
            <label style={{ fontSize: "0.85em" }}>
              Nº aval*<br />
              <input value={draft.avalNumber} onChange={(e) => setDraft({ ...draft, avalNumber: e.target.value })} style={{ width: "8rem" }} />
            </label>
            <label style={{ fontSize: "0.85em" }}>
              Importe*<br />
              <input value={draft.amount} onChange={(e) => setDraft({ ...draft, amount: e.target.value })} style={{ width: "8rem" }} placeholder="0,00" />
            </label>
            <label style={{ fontSize: "0.85em" }}>
              F. constitución<br />
              <input type="date" value={draft.constitutionDate} onChange={(e) => setDraft({ ...draft, constitutionDate: e.target.value })} />
            </label>
            <label style={{ fontSize: "0.85em" }}>
              F. posible vencim.<br />
              <input type="date" value={draft.possibleMaturityDate} onChange={(e) => setDraft({ ...draft, possibleMaturityDate: e.target.value })} />
            </label>
            <label style={{ fontSize: "0.85em" }}>
              Estado<br />
              <select value={draft.status} onChange={(e) => setDraft({ ...draft, status: e.target.value as AvalStatus })}>
                {STATUSES.map((s) => (<option key={s} value={s}>{s}</option>))}
              </select>
            </label>
          </div>
          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "flex-end" }}>
            <label style={{ fontSize: "0.85em", flex: 1, minWidth: "18rem" }}>
              Receptor<br />
              <input value={draft.recipient} onChange={(e) => setDraft({ ...draft, recipient: e.target.value })} style={{ width: "100%" }} />
            </label>
            <label style={{ fontSize: "0.85em" }}>
              CIF receptor<br />
              <input value={draft.recipientCif} onChange={(e) => setDraft({ ...draft, recipientCif: e.target.value })} style={{ width: "9rem" }} />
            </label>
            <label style={{ fontSize: "0.85em" }}>
              CECO<br />
              <input value={draft.costingCode} onChange={(e) => setDraft({ ...draft, costingCode: e.target.value })} style={{ width: "6rem" }} placeholder="0502" />
            </label>
            <label style={{ fontSize: "0.85em" }}>
              Proyecto<br />
              <input value={draft.projectCode} onChange={(e) => setDraft({ ...draft, projectCode: e.target.value })} style={{ width: "6rem" }} placeholder="0000" />
            </label>
          </div>
          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "flex-end" }}>
            <label style={{ fontSize: "0.85em", flex: 1, minWidth: "18rem" }}>
              Motivo<br />
              <input value={draft.reason} onChange={(e) => setDraft({ ...draft, reason: e.target.value })} style={{ width: "100%" }} />
            </label>
            <label style={{ fontSize: "0.85em" }}>
              % apertura<br />
              <input value={draft.openingRatePercent} onChange={(e) => setDraft({ ...draft, openingRatePercent: e.target.value })} style={{ width: "5rem" }} />
            </label>
            <label style={{ fontSize: "0.85em" }}>
              % anual<br />
              <input value={draft.commissionRateAnnual} onChange={(e) => setDraft({ ...draft, commissionRateAnnual: e.target.value })} style={{ width: "5rem" }} />
            </label>
            <label style={{ fontSize: "0.85em" }}>
              Periodicidad<br />
              <select value={draft.periodicity} onChange={(e) => setDraft({ ...draft, periodicity: e.target.value as AvalPeriodicity })}>
                {(Object.keys(PERIODICITY_LABELS) as AvalPeriodicity[]).map((k) => (
                  <option key={k} value={k}>{PERIODICITY_LABELS[k]}</option>
                ))}
              </select>
            </label>
            <label style={{ fontSize: "0.85em" }}>
              Mínimo com.<br />
              <input value={draft.commissionMinimum} onChange={(e) => setDraft({ ...draft, commissionMinimum: e.target.value })} style={{ width: "7rem" }} />
            </label>
          </div>
          <div>
            <button type="button" onClick={save} disabled={busy}>{busy ? "Guardando…" : "Guardar aval"}</button>
          </div>
        </div>
      )}

      {avals.length === 0 && !creating && (
        <p className="meta">No hay avales en esta línea. Añade uno con "+ Nuevo aval".</p>
      )}

      {avals.length > 0 && (
        <div style={{ overflowX: "auto", width: "100%" }}>
          <style>{`
            .aval-tbl { font-size: 0.78em; width: 100%; table-layout: fixed; border-collapse: collapse; }
            .aval-tbl th, .aval-tbl td { padding: 3px 5px; vertical-align: middle; }
            .aval-tbl th { font-weight: 600; text-align: left; background: rgba(120,150,200,0.06); position: sticky; top: 0; border-bottom: 1px solid var(--border, #e5e7eb); }
            .aval-tbl input, .aval-tbl select, .aval-tbl textarea { width: 100%; padding: 2px 4px; font-size: inherit; font-family: inherit; box-sizing: border-box; border: 1px solid transparent; border-radius: 3px; background: transparent; }
            .aval-tbl input:hover, .aval-tbl select:hover, .aval-tbl textarea:hover { border-color: var(--border, #ccd); background: rgba(0,0,0,0.02); }
            .aval-tbl input:focus, .aval-tbl select:focus, .aval-tbl textarea:focus { border-color: #6a8fc9; background: #fff; outline: none; }
            .aval-tbl .ellip { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
            .aval-tbl .num { text-align: right; font-variant-numeric: tabular-nums; }
            .aval-tbl .avalno { font-family: ui-monospace, "Consolas", monospace; font-size: 0.92em; word-break: break-all; line-height: 1.1; }
            .aval-tbl .row-actions button { padding: 2px 6px; font-size: 0.9em; }
            /* Dos filas por aval: la principal (aval-main) sin borde inferior,
               la de motivo (aval-motivo) con borde grueso al final para separar
               un aval del siguiente. */
            .aval-tbl tr.aval-main td { padding-bottom: 1px; }
            .aval-tbl tr.aval-motivo td { padding-top: 0; padding-bottom: 6px; border-bottom: 2px solid var(--border, #d0d5dd); background: rgba(0,0,0,0.015); }
            .aval-tbl tr.aval-motivo .motivo-label { display: inline-block; font-size: 0.85em; opacity: 0.7; margin-right: 6px; padding-left: 4px; min-width: 3.5rem; }
            .aval-tbl tr.aval-motivo textarea { resize: vertical; min-height: 1.6em; line-height: 1.35; overflow: auto; }
          `}</style>
          <table className="aval-tbl">
            <colgroup>
              <col style={{ width: "8rem" }} />
              <col style={{ width: "7.5rem" }} />
              <col style={{ width: "7rem" }} />
              <col />
              <col style={{ width: "6.5rem" }} />
              <col style={{ width: "4rem" }} />
              <col style={{ width: "4rem" }} />
              <col style={{ width: "6rem" }} />
              <col style={{ width: "7.5rem" }} />
              <col style={{ width: "3.8rem" }} />
              <col style={{ width: "3.8rem" }} />
              <col style={{ width: "5.5rem" }} />
              <col style={{ width: "5rem" }} />
              <col style={{ width: "3.5rem" }} />
              <col style={{ width: "4.5rem" }} />
            </colgroup>
            <thead>
              <tr>
                <th>Nº aval</th>
                <th>F. constit.</th>
                <th className="num">Importe</th>
                <th>Receptor</th>
                <th>CIF</th>
                <th>CECO</th>
                <th>Proy.</th>
                <th>Estado</th>
                <th>F. vencim.</th>
                <th className="num">% ap.</th>
                <th className="num">% an.</th>
                <th>Period.</th>
                <th className="num" title="Mínimo anual (se prorratea por período)">Mín./año</th>
                <th>Cuadro</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {avals.map((a) => {
                const patch = editing[a.avalNumber] ?? {};
                const merged = { ...a, ...patch };
                const startEdit = (field: keyof Aval, val: unknown) =>
                  setEditing((prev) => ({ ...prev, [a.avalNumber]: { ...(prev[a.avalNumber] ?? {}), [field]: val } }));
                const commit = (field: keyof Aval, val: unknown) => {
                  if (val === a[field]) {
                    setEditing((prev) => {
                      const copy = { ...prev };
                      const inner = { ...(copy[a.avalNumber] ?? {}) };
                      delete (inner as Record<string, unknown>)[field as string];
                      if (Object.keys(inner).length === 0) delete copy[a.avalNumber];
                      else copy[a.avalNumber] = inner;
                      return copy;
                    });
                    return;
                  }
                  commitField(a.avalNumber, { [field]: val } as Partial<Aval>);
                };
                return (
                  <Fragment key={a.avalNumber}>
                    <tr className="aval-main">
                      <td className="avalno" title={a.avalNumber}>
                        {a.avalNumber}
                        <button
                          type="button"
                          onClick={() => onRename(a)}
                          title="Renombrar nº de aval"
                          style={{ marginLeft: 4, fontSize: "0.7em", padding: "1px 5px", whiteSpace: "nowrap" }}
                        >
                          ✎
                        </button>
                      </td>
                      <td>
                        <input
                          type="date"
                          value={merged.constitutionDate ?? ""}
                          onChange={(e) => startEdit("constitutionDate", e.target.value || null)}
                          onBlur={(e) => commit("constitutionDate", e.target.value || null)}
                        />
                      </td>
                      <td className="num">
                        <NumCell
                          value={merged.amount}
                          allowNull={false}
                          onCommit={(v) => commit("amount", v ?? 0)}
                        />
                      </td>
                      <td className="ellip" title={merged.recipient}>
                        <input
                          value={merged.recipient}
                          onChange={(e) => startEdit("recipient", e.target.value)}
                          onBlur={(e) => commit("recipient", e.target.value)}
                          title={merged.recipient}
                        />
                      </td>
                      <td>
                        <input
                          value={merged.recipientCif}
                          onChange={(e) => startEdit("recipientCif", e.target.value)}
                          onBlur={(e) => commit("recipientCif", e.target.value.toUpperCase())}
                        />
                      </td>
                      <td>
                        <input
                          value={merged.costingCode}
                          onChange={(e) => startEdit("costingCode", e.target.value)}
                          onBlur={(e) => commit("costingCode", e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          value={merged.projectCode}
                          onChange={(e) => startEdit("projectCode", e.target.value)}
                          onBlur={(e) => commit("projectCode", e.target.value)}
                        />
                      </td>
                      <td>
                        <select
                          value={merged.status}
                          onChange={(e) => commit("status", e.target.value as AvalStatus)}
                        >
                          {STATUSES.map((s) => (<option key={s} value={s}>{s}</option>))}
                        </select>
                      </td>
                      <td>
                        <input
                          type="date"
                          value={merged.possibleMaturityDate ?? ""}
                          onChange={(e) => startEdit("possibleMaturityDate", e.target.value || null)}
                          onBlur={(e) => commit("possibleMaturityDate", e.target.value || null)}
                        />
                      </td>
                      <td className="num">
                        <NumCell
                          value={merged.openingRatePercent}
                          onCommit={(v) => commit("openingRatePercent", v)}
                        />
                      </td>
                      <td className="num">
                        <NumCell
                          value={merged.commissionRateAnnual}
                          onCommit={(v) => commit("commissionRateAnnual", v)}
                        />
                      </td>
                      <td>
                        <select
                          value={merged.periodicity}
                          onChange={(e) => commit("periodicity", e.target.value as AvalPeriodicity)}
                        >
                          {(Object.keys(PERIODICITY_LABELS) as AvalPeriodicity[]).map((k) => (
                            <option key={k} value={k}>{PERIODICITY_LABELS[k]}</option>
                          ))}
                        </select>
                      </td>
                      <td className="num">
                        <NumCell
                          value={merged.commissionMinimum}
                          placeholder="—"
                          title="Mínimo ANUAL de comisión (se prorratea por período)"
                          onCommit={(v) => commit("commissionMinimum", v)}
                        />
                      </td>
                      <td>
                        <Link to={`/avales/${encodeURIComponent(bankCode)}/${encodeURIComponent(lineNumber)}/${encodeURIComponent(a.avalNumber)}/cuadro`}>
                          Ver
                        </Link>
                      </td>
                      <td className="row-actions">
                        <button type="button" onClick={() => onDelete(a)}>Borrar</button>
                      </td>
                    </tr>
                    <tr className="aval-motivo">
                      <td colSpan={15}>
                        <span className="motivo-label">Motivo:</span>
                        <textarea
                          rows={1}
                          value={merged.reason}
                          onChange={(e) => startEdit("reason", e.target.value)}
                          onBlur={(e) => commit("reason", e.target.value)}
                          placeholder="—"
                          style={{ display: "inline-block", width: "calc(100% - 4rem)", verticalAlign: "top" }}
                        />
                      </td>
                    </tr>
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
